import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ApiClient } from '../../api/client';
import type { ClassSession } from '../../types';
import { formatSessionDate, formatSessionTime } from '../../lib/format';
import { Spinner } from '../Spinner';
import styles from '../BookingWidget.module.css';

interface SessionStepProps {
  api: ApiClient;
  selectedId: string | null;
  onSelect: (session: ClassSession | null) => void;
}

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

function monthLabel(d: Date): string {
  return new Intl.DateTimeFormat('de-CH', {
    month: 'long',
    year: 'numeric',
  }).format(d);
}

function sessionDayKey(iso: string): string {
  return toYmd(new Date(iso));
}

export function SessionStep({ api, selectedId, onSelect }: SessionStepProps) {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const [month, setMonth] = useState(() => startOfMonth(new Date()));
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const daySessionsRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['sessions'],
    queryFn: () => api.getSessions(),
  });

  useEffect(() => {
    if (!selectedDay) return;
    // Wait for the session list to mount + layout, then scroll it into view
    // so the user sees they still need to pick a course.
    const timer = window.setTimeout(() => {
      daySessionsRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 50);
    return () => window.clearTimeout(timer);
  }, [selectedDay]);

  const sessionsByDay = useMemo(() => {
    const map = new Map<string, ClassSession[]>();
    for (const session of data ?? []) {
      const key = sessionDayKey(session.startsAt);
      const list = map.get(key) ?? [];
      list.push(session);
      map.set(key, list);
    }
    return map;
  }, [data]);

  const calendarDays = useMemo(() => {
    const first = startOfMonth(month);
    // Monday-first grid
    const mondayOffset = (first.getDay() + 6) % 7;
    const gridStart = new Date(first);
    gridStart.setDate(first.getDate() - mondayOffset);

    return Array.from({ length: 42 }).map((_, i) => {
      const date = new Date(gridStart);
      date.setDate(gridStart.getDate() + i);
      const key = toYmd(date);
      const sessions = sessionsByDay.get(key) ?? [];
      const hasOpen = sessions.some((s) => s.seatsAvailable > 0);
      const hasFullOnly =
        sessions.length > 0 && sessions.every((s) => s.seatsAvailable <= 0);
      const inMonth = date.getMonth() === month.getMonth();
      const isPast = date < today;
      return {
        key,
        date,
        day: date.getDate(),
        inMonth,
        isPast,
        hasSessions: sessions.length > 0,
        hasOpen,
        hasFullOnly,
        isToday: key === toYmd(today),
      };
    });
  }, [month, sessionsByDay, today]);

  const daySessions = selectedDay ? (sessionsByDay.get(selectedDay) ?? []) : [];

  function handleDaySelect(key: string) {
    setSelectedDay(key);
    const openSessions = (sessionsByDay.get(key) ?? []).filter(
      (s) => s.seatsAvailable > 0,
    );
    if (openSessions.length === 1) {
      onSelect(openSessions[0]);
      return;
    }
    // Multiple courses: clear any previous pick so "Weiter" stays disabled
    // until the user chooses from the list below.
    onSelect(null);
  }

  if (isLoading) {
    return <Spinner centered large />;
  }

  if (isError) {
    return (
      <div>
        <p className={styles.error}>
          {error instanceof Error
            ? error.message
            : 'Termine konnten nicht geladen werden.'}
        </p>
        <button type="button" className={styles.card} onClick={() => refetch()}>
          Erneut versuchen
        </button>
      </div>
    );
  }

  if (!data?.length) {
    return (
      <p className={styles.empty}>
        Aktuell sind keine Termine verfügbar. Schau bald wieder vorbei.
      </p>
    );
  }

  const canGoPrev = month > startOfMonth(today);

  return (
    <div className={styles.calendarWrap}>
      <div className={styles.calendarNav}>
        <button
          type="button"
          className={styles.calendarNavBtn}
          disabled={!canGoPrev}
          onClick={() => setMonth((m) => addMonths(m, -1))}
          aria-label="Vorheriger Monat"
        >
          ‹
        </button>
        <h3 className={styles.calendarMonth}>{monthLabel(month)}</h3>
        <button
          type="button"
          className={styles.calendarNavBtn}
          onClick={() => setMonth((m) => addMonths(m, 1))}
          aria-label="Nächster Monat"
        >
          ›
        </button>
      </div>

      <div className={styles.calendarWeekdays}>
        {WEEKDAYS.map((d) => (
          <span key={d} className={styles.calendarWeekday}>
            {d}
          </span>
        ))}
      </div>

      <div className={styles.calendarGrid}>
        {calendarDays.map((cell) => {
          const selectable =
            cell.inMonth && !cell.isPast && cell.hasSessions && cell.hasOpen;
          const isSelected = selectedDay === cell.key;
          let className = styles.calendarDay;
          if (!cell.inMonth) className = styles.calendarDayMuted;
          else if (cell.isPast || (!cell.hasSessions && cell.inMonth))
            className = styles.calendarDayEmpty;
          else if (cell.hasFullOnly) className = styles.calendarDayFull;
          else if (cell.hasOpen) className = styles.calendarDayOpen;
          if (isSelected) className = `${className} ${styles.calendarDaySelected}`;
          if (cell.isToday) className = `${className} ${styles.calendarDayToday}`;

          return (
            <button
              key={cell.key}
              type="button"
              className={className}
              disabled={!selectable}
              onClick={() => handleDaySelect(cell.key)}
            >
              <span>{cell.day}</span>
              {cell.hasSessions && cell.inMonth ? (
                <span className={styles.calendarDot} aria-hidden />
              ) : null}
            </button>
          );
        })}
      </div>

      {selectedDay ? (
        <div ref={daySessionsRef} className={styles.daySessions}>
          <div className={styles.daySessionsHeader}>
            <p className={styles.daySessionsHeading}>Kurs wählen</p>
            <p className={styles.daySessionsDate}>
              {daySessions[0]
                ? formatSessionDate(daySessions[0].startsAt)
                : selectedDay}
            </p>
          </div>
          <div className={styles.list}>
            {daySessions.map((session) => {
              const isSelected = session.id === selectedId;
              const isFull = session.seatsAvailable <= 0;

              return (
                <button
                  key={session.id}
                  type="button"
                  className={isSelected ? styles.cardSelected : styles.card}
                  disabled={isFull}
                  onClick={() => onSelect(session)}
                >
                  <h3 className={styles.cardTitle}>{session.title}</h3>
                  <p className={styles.cardMeta}>
                    {formatSessionTime(session.startsAt, session.endsAt)}
                    {session.location ? (
                      <>
                        <br />
                        {session.location}
                      </>
                    ) : null}
                  </p>
                  {isFull ? (
                    <span className={styles.badge}>Ausgebucht</span>
                  ) : (
                    <span className={styles.cardMeta}>
                      {session.seatsAvailable}{' '}
                      {session.seatsAvailable === 1 ? 'Platz frei' : 'Plätze frei'}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <p className={styles.calendarHint}>
          Wähle einen Tag – danach den genauen Kurs.
        </p>
      )}
    </div>
  );
}
